import { html, nothing } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import {
  renderAvatar,
  renderMessageGroup,
  renderReadingIndicatorGroup,
  renderStreamingGroup,
} from "../chat/grouped-render.ts";
import { extractTextCached } from "../chat/message-extract.ts";
import { stripThinkingTags } from "../format.ts";
import { normalizeMessage, normalizeRoleForGrouping } from "../chat/message-normalizer.ts";
import { icons } from "../icons.ts";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import { detectTextDirection } from "../text-direction.ts";
import {
  RENDERED_MARKER,
  type CandidatesPreviewItem,
  type OcrResultCard,
  type ToolRenderItem,
  type ToolRenderPayload,
} from "../app-tool-stream.ts";
import type { SessionsListResult } from "../types.ts";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { ChatAttachment, ChatQueueItem, ChatUploadedFile } from "../ui-types.ts";
import { renderMarkdownSidebar } from "./markdown-sidebar.ts";
import "../components/resizable-divider.ts";
import { t } from "../../i18n/index.ts";

export type CompactionIndicatorStatus = {
  active: boolean;
  startedAt: number | null;
  completedAt: number | null;
};

export type ChatProps = {
  sessionKey: string;
  onSessionKeyChange: (next: string) => void;
  thinkingLevel: string | null;
  showThinking: boolean;
  loading: boolean;
  sending: boolean;
  canAbort?: boolean;
  compactionStatus?: CompactionIndicatorStatus | null;
  toolRenderData?: ToolRenderPayload | null;
  toolRenderSeq?: number | null;
  toolRenderItems?: ToolRenderItem[];
  /** Live SSE: fuzzy candidates before LLM selection (match_quotation single path) */
  candidatePreviews?: CandidatesPreviewItem[];
  /** Gateway/SSE: OCR text cards (image recognition) */
  ocrResultCards?: OcrResultCard[];
  messages: unknown[];
  toolMessages: unknown[];
  stream: string | null;
  streamStartedAt: number | null;
  assistantAvatarUrl?: string | null;
  draft: string;
  queue: ChatQueueItem[];
  connected: boolean;
  canSend: boolean;
  disabledReason: string | null;
  error: string | null;
  sessions: SessionsListResult | null;
  // Focus mode
  focusMode: boolean;
  // Sidebar state
  sidebarOpen?: boolean;
  sidebarContent?: string | null;
  sidebarError?: string | null;
  splitRatio?: number;
  assistantName: string;
  assistantAvatar: string | null;
  // Image attachments
  attachments?: ChatAttachment[];
  onAttachmentsChange?: (attachments: ChatAttachment[]) => void;
  // Excel/PDF file (uploaded, sent as context.file_path)
  uploadedFile?: ChatUploadedFile | null;
  onFileSelect?: (file: File) => Promise<void>;
  onClearUploadedFile?: () => void;
  // Drag-and-drop for Excel/PDF (visual state + drop handler)
  composeDragOver?: boolean;
  onComposeDragOver?: () => void;
  onComposeDragLeave?: () => void;
  onComposeDrop?: (file: File) => void;
  // Scroll control
  showNewMessages?: boolean;
  onScrollToBottom?: () => void;
  // Event handlers
  onRefresh: () => void;
  onToggleFocusMode: () => void;
  onDraftChange: (next: string) => void;
  onSend: () => void;
  onQuickSend?: (text: string) => void;
  onAbort?: () => void;
  onQueueRemove: (id: string) => void;
  onNewSession: () => void;
  onOpenSidebar?: (content: string) => void;
  onCloseSidebar?: () => void;
  onSplitRatioChange?: (ratio: number) => void;
  onChatScroll?: (event: Event) => void;
};

const COMPACTION_TOAST_DURATION_MS = 5000;
const CANDIDATES_PREVIEW_MAX_ROWS = 5;
const ALLOWED_DROP_EXT = /\.(xlsx|xls|xlsm|pdf)$/i;

function formatPricePreview(n: number): string {
  if (!Number.isFinite(n)) {
    return "—";
  }
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function renderCandidatesPreviewCard(
  preview: CandidatesPreviewItem,
  assistant: { name: string; avatar: string | null },
) {
  const rows = preview.candidates;
  const total = rows.length;
  const shown = rows.slice(0, CANDIDATES_PREVIEW_MAX_ROWS);
  const extra = total > CANDIDATES_PREVIEW_MAX_ROWS ? total - CANDIDATES_PREVIEW_MAX_ROWS : 0;
  return html`
    <div class="chat-group assistant">
      ${renderAvatar("assistant", assistant)}
      <div class="chat-group-messages">
        <div class="chat-tool-render chat-candidates-preview" role="status" aria-live="polite">
          <div class="candidates-preview-card">
            <div class="candidates-preview-card__title">
              ${icons.search}
              <span>${t("chat.ui.candidatesPreview.query", { keywords: preview.keywords || "—" })}</span>
            </div>
            <table class="candidates-preview-card__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>${t("chat.ui.candidatesPreview.colCode")}</th>
                  <th>${t("chat.ui.candidatesPreview.colName")}</th>
                  <th>${t("chat.ui.candidatesPreview.colPrice")}</th>
                </tr>
              </thead>
              <tbody>
                ${shown.map(
                  (c, i) => html`
                    <tr>
                      <td>${i + 1}</td>
                      <td>${c.code}</td>
                      <td>${c.matched_name}</td>
                      <td>${formatPricePreview(c.unit_price)}</td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
            ${extra > 0
              ? html`<p class="candidates-preview-card__more">
                  ${t("chat.ui.candidatesPreview.more", { n: String(extra) })}
                </p>`
              : nothing}
            <div class="candidates-preview-card__status">
              ${icons.loader}
              <span>${t("chat.ui.candidatesPreview.selecting", { n: String(total) })}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function pickFirstExcelOrPdf(files: FileList): File | null {
  for (let i = 0; i < files.length; i++) {
    if (ALLOWED_DROP_EXT.test(files[i].name)) return files[i];
  }
  return null;
}

function adjustTextareaHeight(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

function renderCompactionIndicator(status: CompactionIndicatorStatus | null | undefined) {
  if (!status) {
    return nothing;
  }

  // Show "compacting..." while active
  if (status.active) {
    return html`
      <div class="compaction-indicator compaction-indicator--active" role="status" aria-live="polite">
        ${icons.loader} ${t("chat.ui.compaction.active")}
      </div>
    `;
  }

  // Show "compaction complete" briefly after completion
  if (status.completedAt) {
    const elapsed = Date.now() - status.completedAt;
    if (elapsed < COMPACTION_TOAST_DURATION_MS) {
      return html`
        <div class="compaction-indicator compaction-indicator--complete" role="status" aria-live="polite">
          ${icons.check} ${t("chat.ui.compaction.done")}
        </div>
      `;
    }
  }

  return nothing;
}

function generateAttachmentId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function handlePaste(e: ClipboardEvent, props: ChatProps) {
  const items = e.clipboardData?.items;
  if (!items || !props.onAttachmentsChange) {
    return;
  }

  const imageItems: DataTransferItem[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.startsWith("image/")) {
      imageItems.push(item);
    }
  }

  if (imageItems.length === 0) {
    return;
  }

  e.preventDefault();

  for (const item of imageItems) {
    const file = item.getAsFile();
    if (!file) {
      continue;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const dataUrl = reader.result as string;
      const newAttachment: ChatAttachment = {
        id: generateAttachmentId(),
        dataUrl,
        mimeType: file.type,
      };
      const current = props.attachments ?? [];
      props.onAttachmentsChange?.([...current, newAttachment]);
    });
    reader.readAsDataURL(file);
  }
}

function renderAttachmentPreview(props: ChatProps) {
  const attachments = props.attachments ?? [];
  if (attachments.length === 0) {
    return nothing;
  }

  return html`
    <div class="chat-attachments">
      ${attachments.map(
        (att) => html`
          <div class="chat-attachment">
            <img
              src=${att.dataUrl}
              alt=${t("chat.ui.attachments.previewAlt")}
              class="chat-attachment__img"
            />
            <button
              class="chat-attachment__remove"
              type="button"
              aria-label=${t("chat.ui.attachments.remove")}
              @click=${() => {
                const next = (props.attachments ?? []).filter((a) => a.id !== att.id);
                props.onAttachmentsChange?.(next);
              }}
            >
              ${icons.x}
            </button>
          </div>
        `,
      )}
    </div>
  `;
}

function renderUploadedFileRow(props: ChatProps) {
  const file = props.uploadedFile;
  const onSelect = props.onFileSelect;
  const onClear = props.onClearUploadedFile;
  if (file?.file_name) {
    const meta = file.summaryMeta;
    return html`
      <div class="chat-uploaded-file">
        <span class="chat-uploaded-file__name" title=${file.file_path}>${file.file_name}</span>
        <button
          type="button"
          class="btn chat-uploaded-file__clear"
          aria-label=${t("chat.ui.upload.remove")}
          @click=${onClear}
        >
          ${icons.x}
        </button>
        ${meta
          ? html`<span class="chat-uploaded-file__meta">
              ${[
                meta.rows_count != null ? `rows=${meta.rows_count}` : "",
                meta.preview_count != null ? `preview=${meta.preview_count}` : "",
                meta.truncated ? "…truncated" : "",
              ]
                .filter(Boolean)
                .join(" / ")}
            </span>`
          : nothing}
      </div>
    `;
  }
  if (!onSelect || !props.connected) {
    return nothing;
  }
  return html`
    <div class="chat-uploaded-file-row">
      <input
        type="file"
        accept=".xlsx,.xls,.xlsm,.pdf"
        aria-label=${t("chat.ui.upload.label")}
        class="chat-uploaded-file-input"
        @change=${async (e: Event) => {
          const input = e.target as HTMLInputElement;
          const f = input.files?.[0];
          if (f) {
            await onSelect(f);
            input.value = "";
          }
        }}
      />
      <button
        type="button"
        class="btn chat-upload-file-btn"
        @click=${(e: Event) => (e.currentTarget as HTMLElement).parentElement?.querySelector<HTMLInputElement>("input[type=file]")?.click()}
      >
        ${t("chat.ui.upload.button")}
      </button>
    </div>
  `;
}

export function renderChat(props: ChatProps) {
  const canCompose = props.connected;
  const isBusy = props.sending || props.stream !== null;
  const canAbort = Boolean(props.canAbort && props.onAbort);
  const activeSession = props.sessions?.sessions?.find((row) => row.key === props.sessionKey);
  // 未设置 reasoningLevel 的会话：默认展示 reasoning（与显式设为 "off" 区分）
  const showReasoning = props.showThinking && activeSession?.reasoningLevel !== "off";
  const assistantIdentity = {
    name: props.assistantName,
    avatar: props.assistantAvatar ?? props.assistantAvatarUrl ?? null,
  };

  const hasAttachments = (props.attachments?.length ?? 0) > 0;
  const hasUploadedFile = Boolean(props.uploadedFile?.file_name);
  const composePlaceholder = props.connected
    ? hasAttachments
      ? t("chat.ui.compose.placeholder.withImages")
      : t("chat.ui.compose.placeholder.default")
    : t("chat.ui.compose.placeholder.disconnected");

  const splitRatio = props.splitRatio ?? 0.6;
  const sidebarOpen = Boolean(props.sidebarOpen && props.onCloseSidebar);
  const chatItems = buildChatItems(props);
  const assistantGroups = chatItems.filter(
    (x): x is MessageGroup => x.kind === "group" && x.role === "assistant",
  );
  const lastAssistantGroup = assistantGroups.length > 0 ? assistantGroups[assistantGroups.length - 1] : null;
  const thread = html`
    <div
      class="chat-thread"
      role="log"
      aria-live="polite"
      @scroll=${props.onChatScroll}
    >
      ${
        props.loading
          ? html`
              <div class="muted">Loading chat…</div>
            `
          : nothing
      }
      ${repeat(
        chatItems,
        (item) => item.key,
        (item) => {
          if (item.kind === "divider") {
            return html`
              <div class="chat-divider" role="separator" data-ts=${String(item.timestamp)}>
                <span class="chat-divider__line"></span>
                <span class="chat-divider__label">${item.label}</span>
                <span class="chat-divider__line"></span>
              </div>
            `;
          }

          if (item.kind === "reading-indicator") {
            return renderReadingIndicatorGroup(assistantIdentity);
          }

          if (item.kind === "stream") {
            return renderStreamingGroup(
              item.text,
              item.startedAt,
              props.onOpenSidebar,
              assistantIdentity,
            );
          }

          if (item.kind === "candidates-preview") {
            return renderCandidatesPreviewCard(item.preview, assistantIdentity);
          }

          if (item.kind === "ocr-result") {
            return html`
              <div class="chat-group assistant">
                ${renderAvatar("assistant", assistantIdentity)}
                <div class="chat-group-messages">
                  <div class="chat-ocr-card" role="region" aria-label="OCR">
                    <div class="chat-ocr-card__label">📄 图片识别结果</div>
                    <div class="chat-ocr-card__body">
                      ${unsafeHTML(toSanitizedMarkdownHtml(item.card.text))}
                    </div>
                  </div>
                </div>
              </div>
            `;
          }

          if (item.kind === "group") {
            const isLastAssistantGroup =
              item.role === "assistant" &&
              lastAssistantGroup != null &&
              lastAssistantGroup === item &&
              props.stream === null;
            return renderMessageGroup(item, {
              onOpenSidebar: props.onOpenSidebar,
              showReasoning,
              assistantName: props.assistantName,
              assistantAvatar: assistantIdentity.avatar,
              onQuickSend: props.onQuickSend,
              isLastGroup: isLastAssistantGroup,
            });
          }

          return nothing;
        },
      )}
    </div>
  `;

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    props.onComposeDragOver?.();
  };
  const handleDragover = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    props.onComposeDragOver?.();
  };
  const handleDragLeave = (e: DragEvent) => {
    const el = e.currentTarget as HTMLElement;
    const next = e.relatedTarget as Node | null;
    if (next == null) return;
    if (el.contains(next)) return;
    props.onComposeDragLeave?.();
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    props.onComposeDragLeave?.();
    const file = e.dataTransfer?.files?.length ? pickFirstExcelOrPdf(e.dataTransfer.files) : null;
    if (file && props.onComposeDrop) props.onComposeDrop(file);
  };

  return html`
    <section
      class="card chat ${props.composeDragOver ? "chat--drag-over" : ""}"
      @dragenter=${handleDragEnter}
      @dragover=${handleDragover}
      @dragleave=${handleDragLeave}
      @drop=${handleDrop}
    >
      ${props.disabledReason ? html`<div class="callout">${props.disabledReason}</div>` : nothing}

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        props.focusMode
          ? html`
            <button
              class="chat-focus-exit"
              type="button"
              @click=${props.onToggleFocusMode}
              aria-label=${t("chat.ui.compose.exitFocus")}
              title=${t("chat.ui.compose.exitFocus")}
            >
              ${icons.x}
            </button>
          `
          : nothing
      }

      <div
        class="chat-split-container ${sidebarOpen ? "chat-split-container--open" : ""}"
      >
        <div
          class="chat-main"
          style="flex: ${sidebarOpen ? `0 0 ${splitRatio * 100}%` : "1 1 100%"}"
        >
          ${thread}
        </div>

        ${
          sidebarOpen
            ? html`
              <resizable-divider
                .splitRatio=${splitRatio}
                @resize=${(e: CustomEvent) => props.onSplitRatioChange?.(e.detail.splitRatio)}
              ></resizable-divider>
              <div class="chat-sidebar">
                ${renderMarkdownSidebar({
                  content: props.sidebarContent ?? null,
                  error: props.sidebarError ?? null,
                  onClose: props.onCloseSidebar!,
                  onViewRawText: () => {
                    if (!props.sidebarContent || !props.onOpenSidebar) {
                      return;
                    }
                    props.onOpenSidebar(`\`\`\`\n${props.sidebarContent}\n\`\`\``);
                  },
                })}
              </div>
            `
            : nothing
        }
      </div>

      ${
        props.queue.length
          ? html`
            <div class="chat-queue" role="status" aria-live="polite">
              <div class="chat-queue__title">
                ${t("chat.ui.queue.title", { count: String(props.queue.length) })}
              </div>
              <div class="chat-queue__list">
                ${props.queue.map(
                  (item) => html`
                    <div class="chat-queue__item">
                      <div class="chat-queue__text">
                        ${
                          item.attachments?.length
                            ? html`<span class="chat-queue__ocr-status">🔍 识别中…</span>`
                            : item.text || ""
                        }
                      </div>
                      <button
                        class="btn chat-queue__remove"
                        type="button"
                        aria-label=${t("chat.ui.queue.remove")}
                        @click=${() => props.onQueueRemove(item.id)}
                      >
                        ${icons.x}
                      </button>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
          : nothing
      }

      ${renderCompactionIndicator(props.compactionStatus)}

      ${
        props.showNewMessages
          ? html`
            <button
              class="btn chat-new-messages"
              type="button"
              @click=${props.onScrollToBottom}
            >
              ${t("chat.ui.compose.newMessages")} ${icons.arrowDown}
            </button>
          `
          : nothing
      }

      <div class="chat-compose ${props.composeDragOver ? "chat-compose--drag-over" : ""}">
        ${props.composeDragOver
          ? html`<div class="chat-compose__drop-hint">
              ${t("chat.ui.compose.dropHint")}
            </div>`
          : nothing}
        ${renderAttachmentPreview(props)}
        ${renderUploadedFileRow(props)}
        <div class="chat-compose__row">
          <label class="field chat-compose__field">
            <span>${t("chat.ui.compose.label")}</span>
            <textarea
              ${ref((el) => el && adjustTextareaHeight(el as HTMLTextAreaElement))}
              .value=${props.draft}
              dir=${detectTextDirection(props.draft)}
              ?disabled=${!props.connected}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key !== "Enter") {
                  return;
                }
                if (e.isComposing || e.keyCode === 229) {
                  return;
                }
                if (e.shiftKey) {
                  return;
                } // Allow Shift+Enter for line breaks
                if (!props.connected) {
                  return;
                }
                e.preventDefault();
                if (canCompose) {
                  props.onSend();
                }
              }}
              @input=${(e: Event) => {
                const target = e.target as HTMLTextAreaElement;
                adjustTextareaHeight(target);
                props.onDraftChange(target.value);
              }}
              @paste=${(e: ClipboardEvent) => handlePaste(e, props)}
              placeholder=${composePlaceholder}
            ></textarea>
          </label>
          <div class="chat-compose__actions">
            <button
              class="btn"
              ?disabled=${!props.connected || (!canAbort && props.sending)}
              @click=${canAbort ? props.onAbort : props.onNewSession}
            >
              ${canAbort ? t("chat.ui.compose.stop") : t("chat.ui.compose.newSession")}
            </button>
            <button
              class="btn primary"
              ?disabled=${!props.connected}
              @click=${props.onSend}
            >
              ${isBusy ? t("chat.ui.compose.queue") : t("chat.ui.compose.send")}<kbd
                class="btn-kbd"
                >↵</kbd
              >
            </button>
          </div>
        </div>
      </div>
    </section>
  `;
}

const CHAT_HISTORY_RENDER_LIMIT = 200;

function groupMessages(items: ChatItem[]): Array<ChatItem | MessageGroup> {
  const result: Array<ChatItem | MessageGroup> = [];
  let currentGroup: MessageGroup | null = null;

  for (const item of items) {
    if (item.kind !== "message") {
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push(item);
      continue;
    }

    const normalized = normalizeMessage(item.message);
    const role = normalizeRoleForGrouping(normalized.role);
    const timestamp = normalized.timestamp || Date.now();

    // tool_render cards are always standalone — never grouped with adjacent messages
    const openclaw = (item.message as Record<string, unknown>).__openclaw as
      | Record<string, unknown>
      | undefined;
    const isToolRender = openclaw?.kind === "tool_render";
    if (isToolRender) {
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push({
        kind: "group",
        key: `group:${role}:${item.key}`,
        role,
        messages: [{ message: item.message, key: item.key }],
        timestamp,
        isStreaming: false,
      });
      continue;
    }

    if (!currentGroup || currentGroup.role !== role) {
      if (currentGroup) {
        result.push(currentGroup);
      }
      currentGroup = {
        kind: "group",
        key: `group:${role}:${item.key}`,
        role,
        messages: [{ message: item.message, key: item.key }],
        timestamp,
        isStreaming: false,
      };
    } else {
      currentGroup.messages.push({ message: item.message, key: item.key });
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }
  return result;
}

function hasRenderableNonTextBlocks(message: unknown): boolean {
  const m = message as Record<string, unknown>;
  const content = m.content;
  if (!Array.isArray(content)) {
    return false;
  }

  for (const block of content) {
    if (!block || typeof block !== "object") {
      continue;
    }
    const item = block as Record<string, unknown>;
    const kind = typeof item.type === "string" ? item.type.toLowerCase() : "";

    if (
      kind === "toolcall" ||
      kind === "tool_call" ||
      kind === "tooluse" ||
      kind === "tool_use" ||
      kind === "toolresult" ||
      kind === "tool_result"
    ) {
      return true;
    }

    if (typeof item.name === "string" && item.arguments != null) {
      return true;
    }

    if (kind === "file" && typeof item.file_name === "string" && item.file_name.trim()) {
      return true;
    }

    if (kind === "image") {
      if (typeof item.url === "string" && item.url.trim()) {
        return true;
      }
      const source = item.source as Record<string, unknown> | undefined;
      if (
        source?.type === "base64" &&
        typeof source.data === "string" &&
        source.data.trim()
      ) {
        return true;
      }
    }

    if (kind === "image_url") {
      const imageUrl = item.image_url as Record<string, unknown> | undefined;
      if (typeof imageUrl?.url === "string" && imageUrl.url.trim()) {
        return true;
      }
    }
  }

  return false;
}

function shouldSkipEmptyAssistantMessage(message: unknown, normalizedRole: string): boolean {
  if (normalizedRole.toLowerCase() !== "assistant") {
    return false;
  }
  if (hasRenderableNonTextBlocks(message)) {
    return false;
  }
  const visibleText = (extractTextCached(message) ?? "").trim();
  return visibleText.length === 0;
}

function buildChatItems(props: ChatProps): Array<ChatItem | MessageGroup> {
  const items: ChatItem[] = [];
  const renderedCardTexts = new Set<string>();
  const history = Array.isArray(props.messages) ? props.messages : [];
  const tools = Array.isArray(props.toolMessages) ? props.toolMessages : [];
  const renderCards = Array.isArray(props.toolRenderItems) ? props.toolRenderItems : [];
  let pendingCards = [...renderCards].sort((a, b) => a.ts - b.ts);
  if (pendingCards.length === 0 && props.toolRenderData) {
    pendingCards.push({
      id: `legacy:${props.toolRenderSeq ?? Date.now()}`,
      runId: "",
      seq: props.toolRenderSeq ?? 0,
      ts: Date.now(),
      payload: props.toolRenderData,
    });
  }
  const markerAliases = new Set<string>([RENDERED_MARKER, "[已渲染到前端]"]);
  const isRenderedMarker = (text: string) => {
    for (const marker of markerAliases) {
      if (marker && text.startsWith(marker)) {
        return true;
      }
    }
    return false;
  };

  const getInputIndex = (entry: Record<string, unknown>): number => {
    const raw = entry.input_index;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string") {
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const buildBatchSummaryMarkdown = (payload: ToolRenderPayload): string => {
    const resolved = (Array.isArray(payload.resolved_items) ? payload.resolved_items : [])
      .slice()
      .sort((a, b) => getInputIndex(a) - getInputIndex(b));
    const pending = (Array.isArray(payload.pending_items) ? payload.pending_items : [])
      .slice()
      .sort((a, b) => getInputIndex(a) - getInputIndex(b));
    const unmatched = (Array.isArray(payload.unmatched_items) ? payload.unmatched_items : [])
      .slice()
      .sort((a, b) => getInputIndex(a) - getInputIndex(b));
    const lines: string[] = [];
    lines.push("**批处理总表**");
    lines.push("");
    lines.push("| 序号 | 状态 | 产品编号(code) | 产品名称 |");
    lines.push("|---|---|---|---|");
    const rows: Array<{ idx: number; line: string }> = [];
    for (const row of resolved) {
      const chosen = (row.chosen ?? {}) as Record<string, unknown>;
      const idx = getInputIndex(row);
      rows.push({
        idx,
        line: `| ${idx + 1} | matched | ${String(chosen.code ?? "—")} | ${String(chosen.matched_name ?? "—")} |`,
      });
    }
    for (const row of pending) {
      const options = Array.isArray(row.options) ? (row.options as Array<Record<string, unknown>>) : [];
      const top = options[0] ?? {};
      const idx = getInputIndex(row);
      rows.push({
        idx,
        line: `| ${idx + 1} | needs_selection | ${String(top.code ?? "—")} | ${String(top.matched_name ?? "待确认")} |`,
      });
    }
    for (const row of unmatched) {
      const idx = getInputIndex(row);
      rows.push({ idx, line: `| ${idx + 1} | unmatched | — | — |` });
    }
    rows.sort((a, b) => a.idx - b.idx);
    for (const row of rows) {
      lines.push(row.line);
    }
    return lines.join("\n");
  };

  const pushCardFromItem = (card: ToolRenderItem, fallbackTimestamp: number) => {
    const rendered = (
      card.payload.batch_mode
        ? buildBatchSummaryMarkdown(card.payload)
        : (card.payload.formatted_response?.trim() ?? "")
    ).trim();
    if (!rendered) {
      return;
    }
    items.push({
      kind: "message",
      // Stable key based solely on card.id so repeat() reuses the same DOM node
      // whether the card is in live-bottom or history-matched position — prevents
      // DOM remount and fade-in re-animation during the live→history transition.
      key: `tool-render:${props.sessionKey}:${card.id}`,
      message: {
        role: "assistant",
        content: [{ type: "text", text: rendered }],
        timestamp: fallbackTimestamp,
        __openclaw: { kind: "tool_render", runId: card.runId, seq: card.seq },
      },
    });
    renderedCardTexts.add(rendered);
  };

  const historyStart = Math.max(0, history.length - CHAT_HISTORY_RENDER_LIMIT);
  const firstVisibleTimestamp =
    history.length > historyStart
      ? (normalizeMessage(history[historyStart]).timestamp ?? 0)
      : 0;
  const lastVisibleTimestamp =
    history.length > 0
      ? (normalizeMessage(history[history.length - 1]).timestamp ?? 0)
      : 0;
  const historyOcrTexts = new Set<string>();
  const markerIndexList: number[] = [];
  for (let i = historyStart; i < history.length; i++) {
    const msg = history[i];
    const normalized = normalizeMessage(msg);
    const renderedMarkerText = extractTextCached(msg)?.trim() ?? "";
    if (normalized.role.toLowerCase() === "assistant" && isRenderedMarker(renderedMarkerText)) {
      markerIndexList.push(i);
    }
  }
  const markerCardsByIndex = new Map<number, ToolRenderItem[]>();
  if (markerIndexList.length > 0 && pendingCards.length > 0) {
    // Map cards to the nearest following rendered-marker message by timestamp.
    // This supports batch mode (multiple cards for one marker) while keeping
    // "live" cards (arrived before history refresh) as bottom fallback.
    const markerTimeline = markerIndexList.map((index) => {
      const msg = history[index];
      const normalized = normalizeMessage(msg);
      return { index, timestamp: normalized.timestamp ?? 0 };
    });
    const remainingCards: ToolRenderItem[] = [];
    for (const card of pendingCards) {
      const cardTs = typeof card.ts === "number" ? card.ts : 0;
      // Drop cards outside visible history window to avoid stale remounting.
      if (cardTs < firstVisibleTimestamp) {
        continue;
      }
      let bestMarker: { index: number; delta: number } | null = null;
      for (const marker of markerTimeline) {
        const delta = marker.timestamp - cardTs;
        if (delta < 0) {
          continue;
        }
        if (bestMarker == null || delta < bestMarker.delta) {
          bestMarker = { index: marker.index, delta };
        }
      }
      if (!bestMarker) {
        // Keep as bottom fallback only when card is newer than current history tail,
        // i.e. live-arrived before history refresh. Otherwise treat as stale.
        if (cardTs > lastVisibleTimestamp) {
          remainingCards.push(card);
        }
        continue;
      }
      const bucket = markerCardsByIndex.get(bestMarker.index);
      if (bucket) {
        bucket.push(card);
      } else {
        markerCardsByIndex.set(bestMarker.index, [card]);
      }
    }
    for (const cards of markerCardsByIndex.values()) {
      cards.sort((a, b) => a.ts - b.ts);
    }
    pendingCards = remainingCards;
  }

  if (historyStart > 0) {
    items.push({
      kind: "message",
      key: "chat:history:notice",
      message: {
        role: "system",
        content: `Showing last ${CHAT_HISTORY_RENDER_LIMIT} messages (${historyStart} hidden).`,
        timestamp: Date.now(),
      },
    });
  }
  for (let i = historyStart; i < history.length; i++) {
    const msg = history[i];
    const normalized = normalizeMessage(msg);
    const renderedMarkerText = extractTextCached(msg)?.trim() ?? "";
    const raw = msg as Record<string, unknown>;
    const marker = raw.__openclaw as Record<string, unknown> | undefined;
    if (marker && marker.kind === "compaction") {
      items.push({
        kind: "divider",
        key:
          typeof marker.id === "string"
            ? `divider:compaction:${marker.id}`
            : `divider:compaction:${normalized.timestamp}:${i}`,
        label: t("chat.ui.compaction.divider"),
        timestamp: normalized.timestamp ?? Date.now(),
      });
      continue;
    }

    if (marker && marker.kind === "ocr_result" && typeof marker.text === "string") {
      const ocrText = marker.text.trim();
      if (ocrText) {
        historyOcrTexts.add(ocrText);
        items.push({
          kind: "ocr-result",
          key: `history-ocr:${i}:${String(normalized.timestamp ?? 0)}`,
          card: {
            id: `history-ocr-${i}-${String(normalized.timestamp ?? 0)}`,
            text: ocrText,
            createdAt: typeof normalized.timestamp === "number" ? normalized.timestamp : Date.now(),
          },
        });
      }
      continue;
    }

    if (!props.showThinking && normalized.role.toLowerCase() === "toolresult") {
      continue;
    }

    // Replace compact rendered-marker assistant text with the corresponding tool_render card.
    if (
      normalized.role.toLowerCase() === "assistant" &&
      isRenderedMarker(renderedMarkerText)
    ) {
      const cards = markerCardsByIndex.get(i);
      if (cards && cards.length > 0) {
        for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
          const card = cards[cardIndex];
          pushCardFromItem(card, normalized.timestamp ?? Date.now());
        }
      } else {
        items.push({
          kind: "message",
          key: messageKey(msg, i),
          message: msg,
        });
      }
      continue;
    }

    const openclaw = (msg as Record<string, unknown>).__openclaw as Record<string, unknown> | undefined;
    if (openclaw?.kind === "tool_render") {
      const rendered = extractTextCached(msg)?.trim() ?? "";
      if (rendered) {
        renderedCardTexts.add(rendered);
      }
    }

    if (shouldSkipEmptyAssistantMessage(msg, normalized.role)) {
      continue;
    }

    items.push({
      kind: "message",
      key: messageKey(msg, i),
      message: msg,
    });
  }
  if (props.showThinking) {
    for (let i = 0; i < tools.length; i++) {
      items.push({
        kind: "message",
        key: messageKey(tools[i], i + history.length),
        message: tools[i],
      });
    }
  }

  const previews = Array.isArray(props.candidatePreviews) ? props.candidatePreviews : [];
  for (let i = 0; i < previews.length; i++) {
    items.push({
      kind: "candidates-preview",
      key: `candidates-preview:${previews[i].id}`,
      preview: previews[i],
    });
  }

  const ocrCards = Array.isArray(props.ocrResultCards) ? props.ocrResultCards : [];
  for (const card of ocrCards) {
    const body = (card.text ?? "").trim();
    if (!body || historyOcrTexts.has(body)) {
      continue;
    }
    items.push({ kind: "ocr-result", key: card.id, card });
  }

  // Live fallback: cards received before history refresh (no marker in history yet).
  // When history is already loaded for this turn (firstVisibleTimestamp > 0), drop any
  // live card whose timestamp predates the earliest history message — those cards are
  // already represented as virtual tool_render entries inside chatMessages and showing
  // them again would produce duplicate cards at the bottom of the chat.
  const fallbackSeenTexts = new Set<string>();
  for (let i = 0; i < pendingCards.length; i++) {
    const card = pendingCards[i];
    const cardTs = typeof card.ts === "number" ? card.ts : 0;
    if (firstVisibleTimestamp > 0 && cardTs < firstVisibleTimestamp) {
      continue;
    }
    const rendered = card.payload.formatted_response?.trim() ?? "";
    if (!rendered) {
      continue;
    }
    if (renderedCardTexts.has(rendered) || fallbackSeenTexts.has(rendered)) {
      continue;
    }
    fallbackSeenTexts.add(rendered);
    pushCardFromItem(card, card.ts || Date.now());
  }

  if (props.stream !== null) {
    const key = `stream:${props.sessionKey}:${props.streamStartedAt ?? "live"}`;
    // Use filtered text for the length check: if the stream only contains reasoning
    // content (Plan/Gather/Act or <think> blocks), show the reading indicator instead
    // of an empty/invisible streaming bubble.
    const visibleStream = stripThinkingTags(props.stream);
    if (visibleStream.trim().length > 0) {
      items.push({
        kind: "stream",
        key,
        text: props.stream,
        startedAt: props.streamStartedAt ?? Date.now(),
      });
    } else {
      items.push({ kind: "reading-indicator", key });
    }
  }

  return groupMessages(items);
}

function messageKey(message: unknown, index: number): string {
  const m = message as Record<string, unknown>;
  const toolCallId = typeof m.toolCallId === "string" ? m.toolCallId : "";
  if (toolCallId) {
    return `tool:${toolCallId}`;
  }
  const id = typeof m.id === "string" ? m.id : "";
  if (id) {
    return `msg:${id}`;
  }
  const messageId = typeof m.messageId === "string" ? m.messageId : "";
  if (messageId) {
    return `msg:${messageId}`;
  }
  const timestamp = typeof m.timestamp === "number" ? m.timestamp : null;
  const role = typeof m.role === "string" ? m.role : "unknown";
  if (timestamp != null) {
    return `msg:${role}:${timestamp}:${index}`;
  }
  return `msg:${role}:${index}`;
}
